import { useState, useEffect } from 'react';
import { ACLStatusCard, ACLRulesTable, ACLDisableButton } from '../../../components/acl';
import edgeService from '../../../services/edge.service';
import { useTenantStore } from '../../../stores/tenantStore';

interface ACLOverviewProps {
  edgeGuid: string;
  edgeName?: string;
  readOnly?: boolean;
}

const ACLOverview = ({ edgeGuid, edgeName, readOnly = false }: ACLOverviewProps) => {
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [aclEnabled, setAclEnabled] = useState(true);
  const [vppAclStatus, setVppAclStatus] = useState<any>(null);

  useEffect(() => {
    const fetchVppStatus = async () => {
      try {
        const data = await edgeService.getLatestStatus(edgeGuid);
        if (data?.vpp?.acl) {
          // Sanitize null bytes from ACL names (VPP Binary API returns fixed-length C strings with \0 padding)
          const aclStatus = { ...data.vpp.acl };
          if (aclStatus.rules) {
            aclStatus.rules = aclStatus.rules.map((r: any) => ({
              ...r,
              name: r.name?.replace(/\0/g, '') || r.name,
            }));
          }
          setVppAclStatus(aclStatus);
          setAclEnabled((aclStatus.rules?.length || 0) > 0);
        }
      } catch {
        // VPP status not available — components fall back to API-only data
      }
    };
    fetchVppStatus();
  }, [edgeGuid, selectedTenantId]);

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">VPP Access Control Lists</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage firewall rules using VPP native ACL plugin for high-performance packet filtering
          </p>
        </div>
        {!readOnly ? (
          <ACLDisableButton
            edgeGuid={edgeGuid}
            edgeName={edgeName}
            isEnabled={aclEnabled}
            onStatusChange={setAclEnabled}
          />
        ) : (
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${aclEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
              {aclEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        )}
      </div>

      {/* ACL Status Card */}
      <ACLStatusCard edgeGuid={edgeGuid} vppAclStatus={vppAclStatus} />

      {/* ACL Rules Table */}
      <ACLRulesTable edgeGuid={edgeGuid} readOnly={readOnly} vppAclStatus={vppAclStatus} />
    </div>
  );
};

export default ACLOverview;
